import React from "react";
import type { SingleValue } from "react-select";
import t from "../../../../../i18n";
import type { BaseOption } from "@ui/ReactSelect/useFormatOptionLabel";
import { emptyOption } from "@ui/ReactSelect/useFormatOptionLabel";
import type { ApiValue } from "../../Forms/formHelpers";
import { getDefaultValue, multipleValue } from "../../Forms/formHelpers";
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from "../../../../../api/useCategories";
import type { ClassificationFailure } from "../../../../../api/useClassificationFailures";
import { uniq } from "diagnostics-lib/array";
// Degradation (unique value with full option)
export const useDegradationTypeInput = (selectedFailures: ClassificationFailure[]) => {
  const [placeholder, setPlaceholder] = React.useState(
    t("administration.failures.table.header.degradation_type"),
  );
  const degradationTypes = useCategories("DEGRADATION_TYPE");
  const createDegradation = useCreateCategory("DEGRADATION_TYPE");
  const updateDegradation = useUpdateCategory("DEGRADATION_TYPE");
  const deleteDegradation = useDeleteCategory("DEGRADATION_TYPE");
  const [isDirty, setIsDirty] = React.useState(false);

  const degradationTypeIds = uniq(
    selectedFailures
      .map(f => f.degradationTypeId)
      .map(degradationTypeId => ({
        id: degradationTypeId ?? "",
        name: degradationTypeId ?? "",
      })),
  );
  const defaultValue = getDefaultValue<ApiValue>(degradationTypeIds, "id", degradationType => ({
    label: degradationType.name,
    value: degradationType.id,
  }));

  const degradationOptions = [
    { id: emptyOption.value, name: emptyOption.label },
    ...degradationTypes.map(c => ({ id: c, name: c })),
  ];

  const [defaultDegradationOption, setDefaultDegradationOption] = React.useState(() => {
    if (defaultValue?.label === "" || defaultValue?.label === multipleValue.label) return null;
    return defaultValue;
  });

  const [selectedDegradationTypes, setSelectedDegradationTypes] = React.useState<
    SingleValue<BaseOption>
  >(defaultDegradationOption?.label === "" ? null : defaultDegradationOption);

  const handleDegradationChanged = React.useCallback((option: SingleValue<BaseOption>) => {
    setSelectedDegradationTypes(option);
  }, []);

  React.useEffect(() => {
    if (selectedDegradationTypes?.value === defaultDegradationOption?.value) {
      setIsDirty(false);
    } else {
      setIsDirty(true);
    }
  }, [selectedDegradationTypes, defaultDegradationOption]);

  const resetValue = React.useCallback(() => {
    setSelectedDegradationTypes(defaultDegradationOption);
    setIsDirty(false);
  }, [defaultDegradationOption]);

  const handleDegradationCreated = React.useCallback(
    async (newDegradation: string) => {
      await createDegradation(newDegradation);
      setIsDirty(true);

      setSelectedDegradationTypes({
        label: newDegradation,
        value: newDegradation,
      });
    },
    [createDegradation],
  );

  React.useEffect(() => {
    if (defaultValue?.value === multipleValue.value)
      setPlaceholder(t("administration.modal.multiple_value"));
    else if (defaultValue === null || defaultValue.label === "") setPlaceholder(t("form.no_value"));
    else setPlaceholder(t("administration.failures.table.header.degradation_type"));
  }, [defaultValue]);

  const handleDegradationEdited = React.useCallback(
    async (option: BaseOption, label: string) => {
      if (!option.value) return;
      await updateDegradation(option.value, label);
      if (selectedDegradationTypes?.value === option.value) {
        setSelectedDegradationTypes({ ...option, label });
      }
    },
    [updateDegradation, setSelectedDegradationTypes, selectedDegradationTypes],
  );

  const handleDegradationDeleted = React.useCallback(
    async (option: BaseOption) => {
      if (!option.value) return;
      await deleteDegradation(option.value);
      if (selectedDegradationTypes?.value === option.value) {
        setDefaultDegradationOption(null);
        setSelectedDegradationTypes(null);
      }
    },
    [deleteDegradation, selectedDegradationTypes],
  );

  return {
    placeholder,
    degradationOptions,
    selectedDegradationTypes,
    setSelectedDegradationTypes,
    handleDegradationEdited,
    handleDegradationDeleted,
    handleDegradationChanged,
    handleDegradationCreated,
    isDirty,
    resetValue,
  };
};
